import moment, { Moment } from 'moment-timezone';

/**
 * Calculate the minimum allowed date: October 24th 2025, or 180 days ago, whichever is later
 */
export const getMinAllowedDate = (timezone: string): Moment => {
  const oct24_2025 = moment.tz('2025-10-24T00:00:00', timezone);
  const now = moment();
  const oneHundredEightyDaysAgo = now.clone().subtract(180, 'days');
  return moment.max(oct24_2025, oneHundredEightyDaysAgo);
};

/**
 * Get the maximum allowed date (today)
 */
export const getMaxAllowedEndDate = (startDate: Moment): Moment => {
  return startDate.clone().add(180, 'days');
};

/**
 * Validate and normalize a start date
 * @param selectedDate - The date to validate
 * @param endDate - The end date to compare against (optional)
 * @param minAllowedDate - The minimum allowed date
 * @param timezone - The timezone to use for normalization
 * @returns The normalized date if valid, null otherwise
 */
export const validateAndNormalizeStartDate = (
  selectedDate: Moment | null,
  endDate: Moment | null | undefined,
  minAllowedDate: Moment,
  timezone: string,
): Moment | null => {
  if (!selectedDate) {
    return null;
  }

  // Validate that start date is not after end date
  if (endDate && selectedDate.isAfter(endDate, 'day')) {
    return null;
  }

  // Validate that start date is not before minimum allowed date
  if (selectedDate.isBefore(minAllowedDate, 'day')) {
    return null;
  }

  // Normalize to start of day
  return moment.tz(`${selectedDate.format('YYYY-MM-DD')}T00:00:00`, timezone);
};

/**
 * Validate and normalize an end date
 * @param selectedDate - The date to validate
 * @param startDate - The start date to compare against (optional)
 * @param minAllowedDate - The minimum allowed date
 * @param maxAllowedDate - The maximum allowed date
 * @param timezone - The timezone to use for normalization
 * @returns The normalized date if valid, null otherwise
 */
export const validateAndNormalizeEndDate = (
  selectedDate: Moment | null,
  startDate: Moment | null | undefined,
  minAllowedDate: Moment,
  maxAllowedDate: Moment,
  timezone: string,
): Moment | null => {
  if (!selectedDate) {
    return null;
  }

  // Validate that end date is not before start date
  if (startDate && selectedDate.isBefore(startDate, 'day')) {
    return null;
  }

  // Validate that end date is not before minimum allowed date
  if (selectedDate.isBefore(minAllowedDate, 'day')) {
    return null;
  }

  // Validate that end date is not after maximum allowed date
  if (selectedDate.isAfter(maxAllowedDate, 'day')) {
    return null;
  }

  // Normalize to end of day
  return moment.tz(`${selectedDate.format('YYYY-MM-DD')}T23:59:59`, timezone);
};

/**
 * Calculate the maximum allowed days for purchase check: from October 24th 2025 to today, capped at 180 days
 * @param timezone - The timezone to use for date calculations
 * @returns The maximum allowed days (between 0 and 180)
 */
export const getMaxDays = (timezone: string): number => {
  const now = moment();
  const oct24_2025 = moment.tz('2025-10-24T00:00:00', timezone);
  const daysSinceOct24 = now.diff(oct24_2025, 'days');
  // Return the minimum of days since Oct 24, 2025 and 180 (absolute max)
  return Math.min(Math.max(daysSinceOct24, 0), 180);
};

/**
 * Calculate the maximum allowed months for purchase check: from October 24th 2025 to today, capped at 6 months
 * Note: A month is treated as 30 days in this application
 * @param timezone - The timezone to use for date calculations
 * @returns The maximum allowed months (between 1 and 6)
 */
export const getMaxMonths = (timezone: string): number => {
  const maxDays = getMaxDays(timezone);
  const calculatedMonths = Math.floor(maxDays / 30);
  // Return the minimum of calculated months and 6 (absolute max), but at least 1
  const result = Math.min(Math.max(calculatedMonths, 0), 6);
  return result === 0 ? 1 : result;
};

/**
 * Clamp a numeric value to a range between 1 and a maximum value
 * @param value - The input value as a string
 * @param maxValue - The maximum allowed value
 * @returns The clamped value (between 1 and maxValue)
 */
export const clampValue = (value: string, maxValue: number): number => {
  let newValue = Number(value);

  if (Number(value) <= 1) {
    newValue = 1;
  }

  if (Number(value) >= maxValue) {
    newValue = maxValue;
  }

  return newValue;
};
